import { useEffect, useRef, useState } from 'react'
import type { MatchCandidateDto } from '../../api/catalogImportTypes'
import type { ProductTypeDto } from '../../api/productTypes'
import {
  assignRestaurantMyPricesProductType,
  getMyPricesCategorySuggestions,
} from '../../api/restaurantMyPrices'
import type { SupplierCatalogItemDto } from '../../api/supplierCatalog'
import { CatalogItemEditPanel } from '../CatalogItemEditPanel'
import { needsAttention } from './attention'

type ItemCardModalProps = {
  item: SupplierCatalogItemDto
  organizationId: number
  productTypes: ProductTypeDto[]
  brandOptions: string[]
  countryOptions: string[]
  onSaved: (item: SupplierCatalogItemDto) => void
  onDeleted: () => void
  onDismiss: () => void
}

/**
 * Карточка товара окном вместо панели под строкой: панель уезжала вместе с горизонтальной
 * прокруткой таблицы. Поля — те же, что в панели. Если категория под вопросом, над формой
 * подсказка «Возможно: …» — лучший кандидат матчера, кроме текущей категории.
 */
export const ItemCardModal = ({
  item,
  organizationId,
  productTypes,
  brandOptions,
  countryOptions,
  onSaved,
  onDeleted,
  onDismiss,
}: ItemCardModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const onDismissRef = useRef(onDismiss)
  const [current, setCurrent] = useState(item)
  const [alternative, setAlternative] = useState<MatchCandidateDto | null>(null)
  const [applying, setApplying] = useState(false)

  useEffect(() => {
    onDismissRef.current = onDismiss
  }, [onDismiss])

  // Открыватель запоминаем один раз: эффект с onDismiss в зависимостях перезапускался бы на
  // каждый рендер родителя и выдёргивал фокус из поля посреди ввода.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    dialogRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismissRef.current()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      if (opener && opener.isConnected) opener.focus()
    }
  }, [])

  useEffect(() => {
    if (!needsAttention(current)) {
      setAlternative(null)
      return
    }
    let cancelled = false
    void getMyPricesCategorySuggestions(current.id)
      .then((candidates) => {
        if (!cancelled) {
          setAlternative(candidates.find((candidate) => candidate.productTypeId !== current.productTypeId) ?? null)
        }
      })
      .catch(() => {
        if (!cancelled) setAlternative(null)
      })
    return () => {
      cancelled = true
    }
  }, [current])

  const applyAlternative = async () => {
    if (!alternative) return
    setApplying(true)
    try {
      const updated = await assignRestaurantMyPricesProductType(current.id, alternative.productTypeId)
      setCurrent(updated)
      onSaved(updated)
    } finally {
      setApplying(false)
    }
  }

  return (
    <div className="market-paywall-overlay" role="presentation" onClick={onDismiss}>
      <div
        ref={dialogRef}
        className="market-paywall-modal market-supplier-card market-item-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="item-card-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="market-item-card__head">
          <h2 id="item-card-title" className="market-paywall-modal__title">
            {current.supplierName}
          </h2>
          <button type="button" className="echat-btn echat-btn--ghost echat-btn--sm" onClick={onDismiss}>
            Закрыть
          </button>
        </div>
        {alternative ? (
          <div className="market-item-card__hint">
            <span className="market-attention-dot" aria-hidden="true" />
            <span>Категория под вопросом. Возможно:</span>
            <button
              type="button"
              className="market-item-card__hint-action"
              disabled={applying}
              onClick={() => void applyAlternative()}
            >
              {alternative.name}
            </button>
          </div>
        ) : null}
        <CatalogItemEditPanel
          key={`${current.id}:${current.productTypeId ?? 'none'}`}
          item={current}
          organizationId={organizationId}
          productTypes={productTypes}
          brandOptions={brandOptions}
          countryOptions={countryOptions}
          catalogMode="restaurant-my-prices"
          onSaved={(updated) => {
            setCurrent(updated)
            onSaved(updated)
          }}
          onDeleted={onDeleted}
        />
      </div>
    </div>
  )
}
